// « Suivi des paiements » — every booking and its payment status (Encaissement,
// operator 2026-10-24; PMS-TERMINAL-LINK-BRIEF §8.8r).
//
// Every booking the PMS holds — since the takeover, cancelled ones aside —
// current, past and to come, findable by client name, client number, résa or
// channel reference. The tiles on top are the filter: « Impayés » (stays over
// that Vega says are still owed — the list the operator asked to find easily),
// « À régulariser » (owed back, or arrhes Vega still holds and never applied),
// « Notes en attente » (one row per note Vega neither invoiced nor collected on
// a stay otherwise paid — a guest's extras left behind, résa 022177;
// PMS-PAYMENT-FOLLOW-UP-BRIEF §7), « En séjour », « À venir », « Toutes ». How
// each booking reads is bookingBalanceView's: Vega's view once the stay is
// over, the total against what was paid before.
//
// Like Arrivées and Départs, a row opens the Encaissement form on its résa:
// « Arrhes » for a booking to come — the desk types what was received, by
// Virement or VAD as well (the form offers both on this tab) — and
// « Encaisser » with what is owed for the others. A payment taken in the form
// carries the red ⚠ until the feed reads it in Vega, where the desk still keys
// it. « Détail » lists every payment of the booking.
//
// Interim source: the Vega mirror and the Encaissement log. At go-live the
// adapter changes, not this screen.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ArrivalBriefing, BookingBalance, IsoDate, ReservationId } from '@lemontaigu/pms-core';
import { fmtAmount, fmtDate } from './logic.js';
import { dayMonth, displayRoom, nightsBetween, todayParis, vegaReadLabel } from './departureSheet.js';
import type { AdvanceRead } from './advanceView.js';
import {
  BALANCE_PHASE_LABEL_FR,
  BALANCE_STATE_LABEL_FR,
  daysBetween,
  filterTotals,
  inFilter,
  matchesQuery,
  paidWithRecoveries,
  readRows,
  recoveredCents,
  sideNotes,
  soonUnpaid,
  sortRows,
  sortSideNotes,
  stateTone,
  type BalanceFilter,
  type BalanceSort,
  type ReadRow,
  type SideNoteRow,
} from './bookingBalanceView.js';
import { NotInVega, PaymentList } from './PaymentList.js';
import { RoomList } from './RoomList.js';
import { SheetTop } from './SheetTop.js';
import { SortChips, type SortOption } from './SortChips.js';
import { EtablissementTag } from './EtablissementTag.js';

/** What a row asks the form for: arrhes (the desk types the amount received)
 *  or what is owed. */
export type FollowUpBill = 'arrhes' | 'booking';

export interface PaymentFollowUpProps {
  briefing: ArrivalBriefing;
  /** The view chips, placed at the top by the Encaissement screen (§8.8k). */
  nav?: ReactNode;
  /** The Encaissement form, placed under the sheet's title. */
  form?: ReactNode;
  /** Open the Encaissement form on this résa — with this amount, 0 for arrhes. */
  onBill?: (resa: string, kind: FollowUpBill, amountCents: number) => void;
  canBill?: boolean;
  /** Bumped by the form after each payment it records — the sheet re-reads. */
  refreshKey?: number;
  /** Injected for tests; the wall clock otherwise. */
  now?: Date;
}

const SORTS: SortOption<BalanceSort>[] = [
  { key: 'date', label: 'Date' },
  { key: 'name', label: 'Nom' },
  { key: 'due', label: 'Montant' },
];

/** Rows drawn at once — « Toutes » holds the whole book since the takeover. */
export const PAGE_ROWS = 200;

const plural = (n: number) => (n > 1 ? 's' : '');

const TILES: { filter: BalanceFilter; label: string }[] = [
  { filter: 'owed', label: 'Impayés' },
  { filter: 'review', label: 'À régulariser' },
  { filter: 'notes', label: 'Notes en attente' },
  { filter: 'current', label: 'En séjour' },
  { filter: 'future', label: 'À venir' },
  { filter: 'all', label: 'Toutes' },
];

function tileSub(filter: BalanceFilter, t: ReturnType<typeof filterTotals>): string {
  const n = `${t.count} réservation${plural(t.count)}`;
  switch (filter) {
    case 'owed':
      return `${t.count} séjour${plural(t.count)} terminé${plural(t.count)}`;
    case 'review':
      return `${n} · trop-perçu, arrhes`;
    case 'notes':
      return `${t.noteCount} note${plural(t.noteCount)} · ${t.count} séjour${plural(t.count)} réglé${plural(t.count)}`;
    case 'current':
      return `${n} · reste à régler`;
    case 'future':
      return `${n} · ${t.nothingPaid} sans paiement`;
    case 'all':
      return `${n} depuis la reprise`;
  }
}

/** Why a stay over still reads owed or « à régulariser » — Vega's lines. */
function Reasons({ x, today }: { x: ReadRow; today: IsoDate }) {
  const { b, r } = x;
  if (r.phase !== 'past' || b.bookingRef === null) return null;
  const lines: string[] = [];
  for (const n of b.openNotes) {
    const room = n.account === 'GRP' ? ' · groupe' : n.account ? ` · ch. ${displayRoom(n.account)}` : '';
    const who = n.guest ? ` ${n.guest}` : '';
    lines.push(
      n.cents >= 0
        ? `note ${n.facture}${room}${who} · ${fmtAmount(n.cents)}`
        : `note ${n.facture}${room}${who} · à rembourser ${fmtAmount(-n.cents)}`,
    );
  }
  for (const d of b.debts) {
    const left = d.cents - d.recoveredCents;
    if (left !== 0) lines.push(`compte débiteur${d.debtor ? ` ${d.debtor}` : ''} · note ${d.facture} · ${fmtAmount(left)}`);
  }
  if (b.unbilledCents !== 0) {
    lines.push(`non facturé · ${b.unbilledLines} ligne${plural(b.unbilledLines)} · ${fmtAmount(b.unbilledCents)}`);
  }
  if (r.unappliedCents > 0) lines.push(`arrhes non imputées · ${fmtAmount(r.unappliedCents)}`);
  if (lines.length === 0) return null;
  const since = daysBetween(b.departureDate, today);
  return (
    <ul className="pms-pay-list pms-balance-why">
      {lines.map((l) => (
        <li key={l}>{l}</li>
      ))}
      {r.state === 'owed' && <li className="pms-sub">parti depuis {since} j</li>}
    </ul>
  );
}

const sideNoteCount = (b: BookingBalance) => b.openNotes.filter((n) => n.cents > 0).length;

/** « Notes en attente »: one row per side note — what the desk collects,
 *  cancels in Vega, or puts on a debtor account there. */
function SideNotesTable({
  list,
  today,
  empty,
  canBill,
  onBill,
}: {
  list: SideNoteRow[];
  today: IsoDate;
  empty: string | null;
  canBill: boolean;
  onBill?: PaymentFollowUpProps['onBill'];
}) {
  const total = list.reduce((t, n) => t + n.note.cents, 0);
  const stays = new Set(list.map((n) => n.x.b.reservationId)).size;
  return (
    <div className="pms-table-wrap">
      <table className="pms-table" aria-label="Notes en attente">
        <thead>
          <tr>
            <th>Résa</th>
            <th>Client</th>
            <th>Chambre</th>
            <th>Note</th>
            <th>Montant</th>
            <th className="pms-no-print" />
          </tr>
        </thead>
        <tbody>
          {list.map(({ x, note, ageDays }) => {
            const { b } = x;
            const room = note.account === 'GRP' ? 'groupe' : note.account ? `ch. ${displayRoom(note.account)}` : '—';
            return (
              <tr key={`${b.reservationId}:${note.facture}`}>
                <td>
                  {b.bookingRef}
                  <EtablissementTag code={b.propertyCode} />
                </td>
                <td>
                  {b.partyLabel}
                  {b.clientRef && <span className="pms-sub"> ({b.clientRef})</span>}
                  <div className="pms-sub">
                    <span className="pms-nowrap">
                      {dayMonth(b.arrivalDate)} → {dayMonth(b.departureDate)}
                    </span>
                    {b.arrivalDate.slice(0, 4) !== today.slice(0, 4) && ` · ${b.arrivalDate.slice(0, 4)}`}
                  </div>
                </td>
                <td>
                  {room}
                  {note.guest && <div className="pms-sub">{note.guest}</div>}
                </td>
                <td>
                  {note.facture}
                  <div className="pms-sub">
                    {note.date ? fmtDate(note.date) : '—'} · il y a {ageDays} j
                  </div>
                </td>
                <td className="pms-num">{fmtAmount(note.cents)}</td>
                <td className="pms-no-print pms-actions">
                  {canBill && onBill && b.bookingRef !== null && (
                    <button
                      type="button"
                      className="pms-mini"
                      onClick={() => onBill(b.bookingRef!, 'booking', note.cents)}
                      aria-label={`Encaisser la note ${note.facture} de la résa ${b.bookingRef}`}
                    >
                      Encaisser
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {list.length === 0 && empty && (
            <tr>
              <td colSpan={6} className="pms-empty">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
        {list.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={4} className="pms-sub">
                {list.length} note{plural(list.length)} · {stays} séjour{plural(stays)}
              </td>
              <td className="pms-num">{fmtAmount(total)}</td>
              <td className="pms-no-print" />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Status({ x, today }: { x: ReadRow; today: IsoDate }) {
  const { b, r } = x;
  const soon = soonUnpaid(x, today);
  return (
    <>
      <span className={`pms-balance is-${stateTone(r.state)}`}>{BALANCE_STATE_LABEL_FR[r.state]}</span>
      {(r.state === 'owed' || r.state === 'partial' || r.state === 'unpaid') && r.dueCents > 0 && (
        <span className="pms-balance-amt">
          {r.state === 'owed' ? '' : 'reste '}
          <strong>{fmtAmount(r.dueCents)}</strong>
        </span>
      )}
      {r.state === 'review' && r.refundCents > 0 && (
        <span className="pms-balance-amt">à rembourser {fmtAmount(r.refundCents)}</span>
      )}
      {r.overpaidCents > 0 && <span className="pms-sub"> · payé en plus {fmtAmount(r.overpaidCents)}</span>}
      {r.sideNoteCents > 0 && (
        <span
          className="pms-balance-note"
          title="Note que Vega n'a ni facturée ni encaissée, sur un séjour par ailleurs réglé — voir « Notes en attente »"
        >
          {sideNoteCount(b)} note{plural(sideNoteCount(b))} en attente · {fmtAmount(r.sideNoteCents)}
        </span>
      )}
      {soon !== null && (
        <span className="pms-balance-soon" title="Arrivée proche, aucun paiement enregistré">
          {soon === 0 ? "aujourd'hui" : `J-${soon}`} · rien réglé
        </span>
      )}
      {b.pendingCents > 0 && <span className="pms-sub"> · {fmtAmount(b.pendingCents)} en attente</span>}
      <Reasons x={x} today={today} />
    </>
  );
}

export function PaymentFollowUp({ briefing, nav, form, onBill, canBill = false, refreshKey = 0, now }: PaymentFollowUpProps) {
  const [rows, setRows] = useState<BookingBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<BalanceFilter>('owed');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<BalanceSort>('date');
  const [shownRows, setShownRows] = useState(PAGE_ROWS);
  // « Détail »: one booking's payments, read when opened.
  const [open, setOpen] = useState<{ id: ReservationId; read: AdvanceRead } | null>(null);
  // Only the latest read may land.
  const latest = useRef(0);

  const clock = now ?? new Date();
  const today = todayParis(clock);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++latest.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      const r = await briefing.bookingBalances();
      if (mine !== latest.current) return;
      setLoading(false);
      if (!r.ok) {
        setError(r.error.message ?? 'lecture impossible');
        if (!quiet) setRows([]);
        return;
      }
      setError(null);
      setRows(r.value);
    },
    [briefing],
  );

  const readDetail = useCallback(
    (id: ReservationId) => {
      setOpen({ id, read: null });
      void briefing.advance(id).then((r) =>
        setOpen((o) => (o?.id === id ? { id, read: r.ok ? r.value : (r.error.message ?? 'lecture impossible') } : o)),
      );
    },
    [briefing],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // After the form records a payment: re-read quietly, the open detail too.
  const seenKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === seenKey.current) return;
    seenKey.current = refreshKey;
    void load(true);
    if (open) readDetail(open.id);
  }, [refreshKey, load, open, readDetail]);

  const all = useMemo(() => readRows(rows, today), [rows, today]);
  const found = useMemo(() => all.filter((x) => matchesQuery(x.b, query)), [all, query]);
  const shown = useMemo(() => sortRows(found.filter((x) => inFilter(x, filter)), sort, filter), [found, filter, sort]);
  const totals = useMemo(() => new Map(TILES.map((t) => [t.filter, filterTotals(found, t.filter)])), [found]);
  const notes = useMemo(
    () => (filter === 'notes' ? sortSideNotes(sideNotes(shown, today), sort) : []),
    [filter, shown, sort, today],
  );
  const read = vegaReadLabel(rows.find((b) => b.readAt)?.readAt ?? null, clock);

  const onQuery = (q: string) => {
    // A search looks through every booking, not the tile on screen.
    if (query.trim() === '' && q.trim() !== '') setFilter('all');
    setQuery(q);
    setShownRows(PAGE_ROWS);
  };
  const pick = (f: BalanceFilter) => {
    setFilter(f);
    setShownRows(PAGE_ROWS);
  };

  const page = shown.slice(0, shownRows);
  const sum = page.reduce(
    (t, x) => ({ total: t.total + x.b.totalCents, paid: t.paid + paidWithRecoveries(x.b), due: t.due + x.r.dueCents }),
    { total: 0, paid: 0, due: 0 },
  );
  // What an empty list says — nothing while it loads or after an error.
  const empty = (what: string, none: string) =>
    loading || error ? null : query.trim() ? `Aucune ${what} ne correspond à la recherche.` : none;

  return (
    <section className="pms-print" aria-label="Suivi des paiements">
      <SheetTop nav={nav} onRefresh={() => void load()} canPrint={shown.length > 0} />
      <div className="pms-head">
        <div>
          <h2>Suivi des paiements</h2>
          <p className="pms-hint">
            {read && (
              <span className={read.stale ? 'pms-stale' : undefined}>
                {read.stale ? '⚠ ' : ''}
                {read.text}
                {' · '}
              </span>
            )}
            Toutes les réservations depuis la reprise, annulées exceptées · séjour terminé, tel que Vega le dit :
            impayé = séjour laissé sur une note jamais facturée, note mise en compte débiteur et non recouvrée, ou
            lignes jamais facturées ; à régulariser = note négative (trop-perçu à rembourser) ou arrhes encore détenues
            et jamais imputées ; notes en attente = notes que Vega n'a ni facturées ni encaissées sur un séjour par
            ailleurs réglé, souvent les extras d'un membre du groupe · à venir et en séjour : le montant de la
            réservation moins tout ce qui a été réglé, compte débiteur compris · <NotInVega /> pris au formulaire, pas
            encore saisi dans Vega.
          </p>
        </div>
      </div>

      <div className="pms-takings pms-no-print" role="group" aria-label="Filtrer">
        <div className="pms-takings-row">
          {TILES.map((t) => {
            const v = totals.get(t.filter)!;
            return (
              <button
                key={t.filter}
                type="button"
                className={`pms-tile${v.count === 0 ? ' is-empty' : ''}`}
                aria-pressed={filter === t.filter}
                onClick={() => pick(t.filter)}
              >
                <span className="pms-tile-label">{t.label}</span>
                <span className="pms-tile-amt">
                  {t.filter === 'all' ? v.count : v.count === 0 ? '—' : fmtAmount(v.cents)}
                </span>
                <span className="pms-tile-sub">{tileSub(t.filter, v)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {form && <div className="pms-no-print pms-sheet-form">{form}</div>}

      <div className="pms-section-bar pms-no-print">
        <input
          type="search"
          className="pms-search pms-follow-search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Nom, n° client, résa ou réf. OTA"
          aria-label="Rechercher une réservation"
        />
        <SortChips section="Suivi des paiements" value={sort} options={SORTS} onChange={setSort} />
      </div>
      {error && <p className="pms-warn">⚠ {error}</p>}
      {loading && <p className="pms-hint pms-no-print">Chargement…</p>}

      {filter === 'notes' ? (
        <>
          <p className="pms-hint">
            À encaisser, à annuler dans Vega si la prestation n'a pas eu lieu, ou à mettre en compte débiteur dans Vega.
          </p>
          <SideNotesTable
            list={notes}
            today={today}
            empty={empty('note', 'Aucune note en attente.')}
            canBill={canBill}
            onBill={onBill}
          />
        </>
      ) : (
        <>
          <div className="pms-table-wrap">
            <table className="pms-table" aria-label="Réservations et paiements">
              <thead>
                <tr>
                  <th>Résa</th>
                  <th>Client</th>
                  <th>Séjour</th>
                  <th>Montant</th>
                  <th>Réglé</th>
                  <th>Statut</th>
                  <th className="pms-no-print" />
                </tr>
              </thead>
              <tbody>
                {page.map((x) => {
                  const { b, r } = x;
                  const n = nightsBetween(b.arrivalDate, b.departureDate);
                  const isOpen = open?.id === b.reservationId;
                  const canTake = canBill && onBill && b.bookingRef !== null;
                  return (
                    <Fragment key={b.reservationId}>
                      <tr>
                        <td>
                          {b.bookingRef ?? '—'}
                          <span className="pms-sub"> · {BALANCE_PHASE_LABEL_FR[r.phase]}</span>
                          <EtablissementTag code={b.propertyCode} />
                        </td>
                        <td>
                          {b.partyLabel}
                          {b.clientRef && <span className="pms-sub"> ({b.clientRef})</span>}
                          {b.externalRef && <span className="pms-sub"> · réf. {b.externalRef}</span>}
                        </td>
                        <td>
                          <span className="pms-nowrap">
                            {dayMonth(b.arrivalDate)} → {dayMonth(b.departureDate)}
                          </span>
                          <span className="pms-sub">
                            {' '}
                            · {n} nuit{plural(n)}
                            {b.arrivalDate.slice(0, 4) !== today.slice(0, 4) && ` · ${b.arrivalDate.slice(0, 4)}`}
                          </span>
                          <div className="pms-sub">
                            <RoomList rooms={b.rooms} />
                          </div>
                        </td>
                        <td className="pms-num">{b.totalCents ? fmtAmount(b.totalCents) : '—'}</td>
                        <td className="pms-num">
                          {paidWithRecoveries(b) ? fmtAmount(paidWithRecoveries(b)) : '—'}
                          {b.arrhesCents !== 0 && <div className="pms-sub">dont arrhes {fmtAmount(b.arrhesCents)}</div>}
                          {recoveredCents(b) !== 0 && (
                            <div className="pms-sub">dont compte débiteur {fmtAmount(recoveredCents(b))}</div>
                          )}
                          {b.collectedCents !== 0 && (
                            <div className="pms-sub">
                              <NotInVega /> {fmtAmount(b.collectedCents)} pas encore dans Vega
                            </div>
                          )}
                          {b.depositUnattributedCents !== 0 && (
                            <div className="pms-sub" title="Arrhes d'un client ayant plusieurs réservations : à vérifier dans Vega">
                              + {fmtAmount(b.depositUnattributedCents)} à vérifier
                            </div>
                          )}
                        </td>
                        <td>
                          <Status x={x} today={today} />
                        </td>
                        <td className="pms-no-print pms-actions">
                          <button
                            type="button"
                            className="pms-mini"
                            aria-expanded={isOpen}
                            onClick={() => (isOpen ? setOpen(null) : readDetail(b.reservationId))}
                          >
                            Détail
                          </button>
                          {canTake && r.phase === 'future' && (
                            <button
                              type="button"
                              className="pms-mini"
                              onClick={() => onBill(b.bookingRef!, 'arrhes', 0)}
                              aria-label={`Enregistrer des arrhes pour la résa ${b.bookingRef}`}
                            >
                              Arrhes
                            </button>
                          )}
                          {canTake && r.phase !== 'future' && r.dueCents > 0 && (
                            <button
                              type="button"
                              className="pms-mini"
                              onClick={() => onBill(b.bookingRef!, 'booking', r.dueCents)}
                              aria-label={`Encaisser la résa ${b.bookingRef}`}
                            >
                              Encaisser
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="pms-follow-detail">
                          <td colSpan={7}>
                            {open.read === null ? (
                              <span className="pms-hint">Lecture des paiements…</span>
                            ) : typeof open.read === 'string' ? (
                              <span className="pms-warn">⚠ {open.read}</span>
                            ) : open.read.payments.length === 0 ? (
                              <span className="pms-hint">Aucun paiement enregistré sur cette réservation.</span>
                            ) : (
                              <>
                                <span className="pms-sub">Paiements de la réservation</span>
                                <PaymentList payments={open.read.payments} />
                              </>
                            )}
                            {b.lastPaidOn && (
                              <span className="pms-sub"> · dernier le {fmtDate(b.lastPaidOn)}</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {shown.length === 0 && empty('réservation', 'Aucune réservation ici.') && (
                  <tr>
                    <td colSpan={7} className="pms-empty">
                      {empty('réservation', 'Aucune réservation ici.')}
                    </td>
                  </tr>
                )}
              </tbody>
              {page.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={3} className="pms-sub">
                      {shown.length} réservation{plural(shown.length)}
                      {page.length < shown.length ? ` · ${page.length} affichées` : ''}
                    </td>
                    <td className="pms-num">{fmtAmount(sum.total)}</td>
                    <td className="pms-num">{fmtAmount(sum.paid)}</td>
                    <td>
                      {sum.due > 0 && (
                        <>
                          reste <strong>{fmtAmount(sum.due)}</strong>
                        </>
                      )}
                    </td>
                    <td className="pms-no-print" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {page.length < shown.length && (
            <p className="pms-no-print">
              <button type="button" className="pms-mini" onClick={() => setShownRows((k) => k + PAGE_ROWS)}>
                Afficher {Math.min(PAGE_ROWS, shown.length - page.length)} de plus
              </button>{' '}
              <span className="pms-sub">ou affinez la recherche</span>
            </p>
          )}
        </>
      )}
    </section>
  );
}
